import React, { Fragment } from 'react';

import { Breadcrumb, Col, Row } from 'antd';
import { config } from 'config/config';
import routePaths, { deprecatedRoutePaths } from 'config/routes';
import { NextPage } from 'next';
import { NextRouter, useRouter } from 'next/router';
import { renderConsumerBreadcrumbs } from 'src/components/breadcrumb/BreadcrumbConsumer';
import Link from 'src/components/Link';
import { IBreadCrumb } from 'src/shared/models';
import { shouldRenderBreadCrumb, shouldRenderConBreadCrumb } from 'utils';

interface OwnProps {
  breadCrumbs: IBreadCrumb[];
}

const CustomBreadcrumb: NextPage<OwnProps> = ({ breadCrumbs }): JSX.Element => {
  const router = useRouter();
  if (shouldRenderConBreadCrumb(router.pathname))
    return renderConsumerBreadcrumbs(router.pathname, breadCrumbs);

  const allowedRoutes: string[] = [
    routePaths.PAGES.CAMPAIGN_DETAIL,
    routePaths.PAGES.CAMPAIGN,
    routePaths.PAGES.MARKETING_MATERIALS,
    routePaths.PAGES.REQUEST_ORDER,
    routePaths.PAGES.TALK_DETAIL,
    routePaths.PAGES.TALK,
    routePaths.PAGES.TRAINING_DETAIL,
    routePaths.PAGES.TRAINING,
    routePaths.PAGES.EXPERIENCE,
    routePaths.PAGES.EXPERIENCE_DETAIL,
    routePaths.PAGES.CHALLENGE,
    routePaths.PAGES.CHALLENGE_DETAIL,
    routePaths.PAGES.FORMATION,
    routePaths.PAGES.FORMATION_DETAIL,
    routePaths.PAGES.DONATION,
    routePaths.PAGES.DONATION_DETAIL,
    deprecatedRoutePaths.PAGES.TALKS,
    deprecatedRoutePaths.PAGES.TRAININGS,
  ];

  const generateBreadcrumbs = (
    _pathname: string,
    breadCrumbRoutes: IBreadCrumb[]
  ): JSX.Element =>
    allowedRoutes.indexOf(_pathname) > -1 ? (
      <Row justify="start" className="breadcrumb-con">
        <Breadcrumb separator=">">
          {breadCrumbRoutes.map(({ pathRoute, pathName }, index) => (
            <Breadcrumb.Item key={index}>
              {BreadcrumbTitle(
                pathName,
                pathRoute,
                index == breadCrumbRoutes.length - 1
              )}
            </Breadcrumb.Item>
          ))}
        </Breadcrumb>
      </Row>
    ) : null;

  const BreadcrumbTitle = (
    pathName: string,
    pathRoute: string,
    isLatest: boolean
  ) => {
    const applyLink = pathRoute.length > 0 && !isLatest;
    const title = <a className="breadcrumb-link">{pathName}</a>;

    return applyLink ? <Link href={pathRoute}>{title}</Link> : title;
  };

  const mainPage = breadCrumbs.find((item) => item.isMain);

  let showOnMobile = true;

  switch (router.pathname) {
    case routePaths.PAGES.EXPERIENCE_DETAIL:
    case routePaths.PAGES.CHALLENGE_DETAIL:
    case routePaths.PAGES.DONATION_DETAIL:
    case routePaths.PAGES.FORMATION_DETAIL:
      showOnMobile = false;
      break;
  }

  return (
    breadCrumbs.length > 0 && (
      <div
        className={`content-breadcrumb ${
          showOnMobile ? '' : 'not_show_on_breakpoint-md'
        }`}
      >
        <div className={`container challengePage experiencesPage`}>
          <div className="container">
            <Row justify="center">
              <Col span={24}>
                {generateBreadcrumbs(router.pathname, breadCrumbs)}
                {!mainPage.icon ? null : (
                  <Row className="content-breadcrumb-title" align="middle">
                    <img
                      loading="lazy"
                      className="breadcrumb-icon"
                      src={mainPage.icon}
                    />
                    <h3 className="breadcrumb-title">{mainPage.pathName}</h3>
                  </Row>
                )}
              </Col>
            </Row>
          </div>
        </div>
      </div>
    )
  );
};

export default CustomBreadcrumb;

export const buildBreadCrumb = (pathName: string): IBreadCrumb => {
  return {
    icon: null,
    pathName,
    pathRoute: '',
    isMain: false,
  };
};

export const getBreadCrumbs = (
  router: NextRouter,
  formatMessage: any
): IBreadCrumb[] => {
  const breadCrumbRoutes: IBreadCrumb[] = [];
  let pathRoute = 'NOT DEFINED';
  let pathName = router.pathname;
  let icon = null;

  switch (router.pathname) {
    case routePaths.PAGES.CAMPAIGN:
    case routePaths.PAGES.CAMPAIGN_DETAIL:
      pathRoute = routePaths.PAGES.CAMPAIGN;
      pathName = 'app.campaigns';
      icon = `${config.APP.BASE_URL}/campaign-map.svg`;
      break;
    case routePaths.PAGES.TALK:
    case routePaths.PAGES.TALK_DETAIL:
    case deprecatedRoutePaths.PAGES.TALKS:
      pathRoute = routePaths.PAGES.TALK;
      pathName = 'app.events';
      icon = `${config.APP.ASSET_PREFIX}/events.svg`;
      break;
    case routePaths.PAGES.TRAINING:
    case routePaths.PAGES.TRAINING_DETAIL:
    case deprecatedRoutePaths.PAGES.TRAININGS:
      pathRoute = routePaths.PAGES.TRAINING;
      pathName = 'page.training.title';
      icon = `${config.APP.ASSET_PREFIX}/challenge.svg`;
      break;
    case routePaths.PAGES.REQUEST_ORDER:
      pathName = 'page.request-order.title';
      pathRoute = routePaths.PAGES.REQUEST_ORDER;
      icon = `${config.APP.ASSET_PREFIX}/order_2.svg`;
      break;
    case routePaths.PAGES.MARKETING_MATERIALS:
      pathName = 'page.marketing-materials.title';
      pathRoute = routePaths.PAGES.MARKETING_MATERIALS;
      icon = `${config.APP.ASSET_PREFIX}/marketing-materials.svg`;
      break;
    case routePaths.PAGES.EXPERIENCE:
    case routePaths.PAGES.EXPERIENCE_DETAIL:
      pathName = 'page.experience.title';
      pathRoute = routePaths.PAGES.EXPERIENCE;
      break;
    case routePaths.PAGES.FORMATION:
    case routePaths.PAGES.FORMATION_DETAIL:
      pathName = 'page.formation.title';
      pathRoute = routePaths.PAGES.FORMATION;
      break;
    case routePaths.PAGES.CHALLENGE:
    case routePaths.PAGES.CHALLENGE_DETAIL:
      pathName = 'page.contest.title';
      pathRoute = routePaths.PAGES.CHALLENGE;
      break;
    case routePaths.PAGES.DONATION:
    case routePaths.PAGES.DONATION_DETAIL:
      pathName = 'page.donation.title';
      pathRoute = routePaths.PAGES.DONATION;
      break;
  }

  const mainPage: IBreadCrumb = {
    pathRoute,
    pathName: formatMessage({ id: pathName }),
    icon,
    isMain: true,
  };

  breadCrumbRoutes.push({
    pathRoute: '/',
    pathName: formatMessage({ id: 'app.home' }),
    icon: null,
  });

  breadCrumbRoutes.push(mainPage);

  return breadCrumbRoutes;
};
